// src/pages/LabelDesignsListPage.js
// Label Designs list page — route: /label-designs
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import productLabelService from '../services/productLabelService';

// ─── Pizzacini Default Template ───────────────────────────────────────────────
// Uses the "columns" (panels) format so Nutrition Facts spans full height.

const DEFAULT_TEMPLATE = {
  width: 6,
  height: 4,
  columns: [
    {
      id: 'panel-left',
      flex: 2,
      rows: [
        {
          id: 'r1',
          flex: 1.5,
          columns: [{
            id: 'c1',
            flex: 1,
            blocks: [
              { id: 'b1', type: 'product_name',        style: { fontSize: 22, fontWeight: 'bold', textAlign: 'center', marginBottom: 2 } },
              { id: 'b2', type: 'net_weight',           style: { fontSize: 13, textAlign: 'center', marginBottom: 2 } },
              { id: 'b3', type: 'storage_banner',       style: { fontSize: 13, fontWeight: 'bold', textAlign: 'center', marginBottom: 4 } },
              { id: 'b4', type: 'cooking_instructions', style: { fontSize: 9,  textAlign: 'left',   marginBottom: 4 } },
              { id: 'b5', type: 'ingredients',          style: { fontSize: 10, textAlign: 'left',   marginBottom: 3 } },
              { id: 'b6', type: 'allergen_statement',   style: { fontSize: 11, fontWeight: 'bold',  textAlign: 'left' } },
            ],
          }],
        },
        {
          id: 'r2',
          flex: 1,
          columns: [
            { id: 'c2', flex: 1, blocks: [
              { id: 'b7',  type: 'upc_barcode',       style: { marginBottom: 4 } },
              { id: 'b8',  type: 'upc_text',          style: { fontSize: 9, textAlign: 'center', marginBottom: 8 } },
              { id: 'b9',  type: 'manufacturer_info', style: { fontSize: 9, textAlign: 'left' } },
            ]},
            { id: 'c3', flex: 1, blocks: [
              { id: 'b10', type: 'lot_exp_box',        style: { fontSize: 13, textAlign: 'center', marginBottom: 6 } },
              { id: 'b11', type: 'qr_code',            style: { marginBottom: 4 } },
              { id: 'b12', type: 'brand_logo',         style: { marginBottom: 4 } },
              { id: 'b13', type: 'country_of_origin',  style: { fontSize: 9, textAlign: 'center' } },
            ]},
          ],
        },
      ],
    },
    {
      id: 'panel-right',
      flex: 1,
      blocks: [
        { id: 'b14', type: 'nutrition_facts', bilingual: true, style: { fontSize: 7 } },
      ],
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

// Deep clone a layout (columns or rows format) with fresh IDs
function deepCloneLayout(layout) {
  const cloneBlock = b => ({ ...b, id: 'b' + uid() });
  const cloneCol   = c => ({ ...c, id: 'c' + uid(), blocks: (c.blocks || []).map(cloneBlock) });
  const cloneRow   = r => ({ ...r, id: 'r' + uid(), columns: (r.columns || []).map(cloneCol) });
  const clonePanel = p => ({
    ...p,
    id: 'p' + uid(),
    ...(p.rows   ? { rows:   p.rows.map(cloneRow)   } : {}),
    ...(p.blocks ? { blocks: p.blocks.map(cloneBlock) } : {}),
  });

  if (layout.columns) {
    return { ...layout, columns: layout.columns.map(clonePanel) };
  }
  if (layout.rows) {
    return { ...layout, rows: layout.rows.map(cloneRow) };
  }
  return { ...layout };
}

// Count total blocks in any layout format
function countBlocks(layout) {
  let count = 0;
  if (layout.columns) {
    for (const panel of layout.columns) {
      if (panel.blocks) count += panel.blocks.length;
      if (panel.rows) for (const row of panel.rows) for (const col of (row.columns || [])) count += (col.blocks || []).length;
    }
  } else if (layout.rows) {
    for (const row of layout.rows) for (const col of (row.columns || [])) count += (col.blocks || []).length;
  }
  return count;
}

// ─── Page Component ───────────────────────────────────────────────────────────

function LabelDesignsListPage() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [cloningId, setCloningId] = useState(null);

  const load = () => {
    setLoading(true);
    productLabelService.getDesigns()
      .then(setDesigns)
      .catch(() => setError('Failed to load designs.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDefaultTemplate = async () => {
    try {
      const cloned = deepCloneLayout(DEFAULT_TEMPLATE);
      const created = await productLabelService.createDesign({
        name: 'Pizzacini Default Label',
        width: DEFAULT_TEMPLATE.width,
        height: DEFAULT_TEMPLATE.height,
        rows: cloned,          // backend stores the whole layout object in `rows` field
      });
      navigate(`/label-designs/${created.id}/edit`);
    } catch {
      setError('Failed to create default template.');
    }
  };

  const handleClone = async (design) => {
    setCloningId(design.id);
    try {
      const layout = design.rows || {};
      const cloned = deepCloneLayout(layout);
      await productLabelService.createDesign({
        name: `Copy of ${design.name}`,
        width: parseFloat(design.width) || 6,
        height: parseFloat(design.height) || 4,
        rows: cloned,
      });
      load();
    } catch {
      setError('Failed to clone design.');
    } finally {
      setCloningId(null);
    }
  };

  const handleDelete = async (design) => {
    if (!window.confirm(`Delete design "${design.name}"? This cannot be undone.`)) return;
    setDeletingId(design.id);
    try {
      await productLabelService.deleteDesign(design.id);
      setDesigns(d => d.filter(x => x.id !== design.id));
    } catch {
      setError('Failed to delete design.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/labels" className="text-muted" style={{ fontSize: 12 }}>← Labels</Link>
          <h4 className="mb-0 mt-1">Label Designs</h4>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={handleDefaultTemplate}>
            Default Template
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/label-designs/new')}>
            + New Design
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show py-2" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading designs…
        </div>
      ) : designs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40 }}>🎨</div>
          <p className="mt-2">No label designs yet.</p>
          <p style={{ fontSize: 13 }}>
            Click <strong>+ New Design</strong> to start from scratch, or{' '}
            <strong>Default Template</strong> to use the standard Pizzacini layout.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <button className="btn btn-outline-secondary btn-sm" onClick={handleDefaultTemplate}>Default Template</button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/label-designs/new')}>+ New Design</button>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Size (in)</th>
                <th>Blocks</th>
                <th>Panels / Rows</th>
                <th style={{ width: 180 }}></th>
              </tr>
            </thead>
            <tbody>
              {designs.map(design => {
                const layout = design.rows || {};
                const w = parseFloat(design.width) || '?';
                const h = parseFloat(design.height) || '?';
                const blocks = countBlocks(layout);
                const topLevel = layout.columns
                  ? `${layout.columns.length} panel${layout.columns.length !== 1 ? 's' : ''}`
                  : layout.rows
                    ? `${layout.rows.length} row${layout.rows.length !== 1 ? 's' : ''}`
                    : '—';

                return (
                  <tr key={design.id}>
                    <td><span className="fw-semibold">{design.name}</span></td>
                    <td><span className="badge bg-light text-dark border">{w}" × {h}"</span></td>
                    <td>{blocks}</td>
                    <td><span className="text-muted" style={{ fontSize: 12 }}>{topLevel}</span></td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/label-designs/${design.id}/edit`} className="btn btn-outline-primary btn-sm me-1">Edit</Link>
                      <button className="btn btn-outline-secondary btn-sm me-1"
                        onClick={() => handleClone(design)} disabled={cloningId === design.id}>
                        {cloningId === design.id ? '…' : 'Clone'}
                      </button>
                      <button className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(design)} disabled={deletingId === design.id}>
                        {deletingId === design.id ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LabelDesignsListPage;
